import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff, Check, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";

function ShieldLogo({ className = "h-10 w-10" }) {
    return (
        <svg viewBox="0 0 40 44" className={className} fill="none" aria-hidden="true">
            <defs>
                <linearGradient id="lShield" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0EA5A4" />
                    <stop offset="100%" stopColor="#0F766E" />
                </linearGradient>
            </defs>
            <path
                d="M20 2 L36 8 V20 C36 30 28 38 20 42 C12 38 4 30 4 20 V8 Z"
                fill="url(#lShield)"
                stroke="#D4AF37"
                strokeWidth="1.4"
            />
            <path
                d="M11 18 Q15 14 20 18 T29 18 M11 23 Q15 19 20 23 T29 23 M11 28 Q15 24 20 28 T29 28"
                stroke="#D4AF37"
                strokeWidth="1.8"
                strokeLinecap="round"
                fill="none"
            />
        </svg>
    );
}

function GoogleIcon() {
    return (
        <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.7-6.2 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.1c-2 1.5-4.5 2.4-7.2 2.4-5.1 0-9.5-3.2-11.2-7.8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.2 5.7l6.2 5.1C40.3 36.8 44 31 44 24c0-1.3-.1-2.4-.4-3.5z" />
        </svg>
    );
}

const PERKS = [
    "Save 2+ hours per tender analysis",
    "All Language Summary in every plan",
    "Trusted by 1,200+ Indian businesses",
];

export default function Login() {
    const { signInWithEmail, signInWithGoogle } = useAuth();
    const [show, setShow] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const from = location.state?.from || "/dashboard";

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await signInWithEmail({ email, password });
            toast.success("Welcome back!");
            navigate(from, { replace: true });
        } catch (err) {
            toast.error(err.message || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogle = async () => {
        setGoogleLoading(true);
        try {
            await signInWithGoogle();
        } catch (err) {
            toast.error(err.message || "Google sign-in failed");
            setGoogleLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#071A2F] text-white">
            {/* BRANDING SIDE */}
            <div className="hidden lg:flex relative bg-gradient-to-br from-[#0F766E] via-[#0E6760] to-[#0A4842] text-white p-14 flex-col justify-between overflow-hidden">
                <div className="absolute inset-0 grid-pattern-dark opacity-25" />
                <div className="absolute -top-40 -left-32 h-[28rem] w-[28rem] rounded-full bg-gold/20 blur-3xl pulse-glow" />
                <div className="absolute -bottom-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-teal-400/25 blur-3xl pulse-glow" />

                <Link to="/" className="relative flex items-center gap-2.5 z-10">
                    <ShieldLogo />
                    <span className="font-bold text-lg tracking-tight">
                        TenderTrack<span className="text-gold">AI</span>
                    </span>
                </Link>

                <div className="relative">
                    <div className="flex items-center gap-1 mb-5">
                        {["s1", "s2", "s3", "s4", "s5"].map((s) => (
                            <Star key={s} className="h-4 w-4 fill-gold text-gold" />
                        ))}
                    </div>
                    <h2 className="font-serif text-3xl lg:text-4xl font-bold uppercase leading-[1.1] text-balance max-w-md">
                        &ldquo;TenderTrackAI saved my team 60+ hours last month. We bid on
                        3x more tenders now.&rdquo;
                    </h2>
                    <div className="mt-7 flex items-center gap-3">
                        <div className="h-11 w-11 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center font-bold text-gold">
                            RK
                        </div>
                        <div>
                            <p className="font-bold text-sm">Rajesh Kumar</p>
                            <p className="text-xs text-teal-100">
                                Civil Contractor · Jaipur
                            </p>
                        </div>
                    </div>
                </div>

                <div className="relative space-y-3">
                    {PERKS.map((p) => (
                        <div key={p} className="flex items-center gap-3 text-sm">
                            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gold/20 border border-gold/40">
                                <Check className="h-3 w-3 text-gold" />
                            </div>
                            {p}
                        </div>
                    ))}
                </div>
            </div>

            {/* FORM SIDE */}
            <div className="relative flex flex-col p-6 sm:p-10 lg:p-14">
                <div className="absolute inset-0 mesh-hero opacity-40 pointer-events-none" />
                <div className="absolute inset-0 grid-pattern-dark opacity-30 pointer-events-none" />

                <Link
                    to="/"
                    className="relative flex items-center gap-2.5 lg:hidden z-10"
                >
                    <ShieldLogo />
                    <span className="font-bold text-lg">
                        TenderTrack<span className="gradient-text-teal">AI</span>
                    </span>
                </Link>

                <div className="relative flex-1 flex items-center justify-center py-10 z-10">
                    <div className="w-full max-w-md">
                        <div className="badge-pill border border-gold/30 bg-gold/5 text-gold">
                            <Star className="h-3 w-3 fill-gold" /> Welcome back
                        </div>

                        <h1 className="mt-6 font-serif text-4xl lg:text-5xl font-bold uppercase tracking-tight leading-[1.05] text-balance">
                            Log in to <span className="gradient-text-teal">TenderTrackAI</span>
                        </h1>
                        <p className="mt-3 text-slate-400">
                            Continue analyzing tenders smarter.
                        </p>

                        <button
                            type="button"
                            onClick={handleGoogle}
                            disabled={googleLoading}
                            className="mt-8 w-full h-12 rounded-2xl border border-white/15 bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center font-semibold text-sm disabled:opacity-60"
                            data-testid="login-google-btn"
                        >
                            <GoogleIcon /> {googleLoading ? "Redirecting…" : "Continue with Google"}
                        </button>

                        <div className="mt-7 flex items-center gap-3">
                            <div className="h-px flex-1 bg-white/10" />
                            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                                or
                            </span>
                            <div className="h-px flex-1 bg-white/10" />
                        </div>

                        <form onSubmit={submit} className="mt-7 space-y-4">
                            <div>
                                <Label
                                    htmlFor="email"
                                    className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-300"
                                >
                                    Email
                                </Label>
                                <Input
                                    id="email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@company.in"
                                    className="mt-2 h-12 rounded-xl bg-white/5 border-white/15 placeholder:text-slate-500 text-white focus-visible:ring-teal-500/40 focus-visible:border-teal-500/40"
                                    data-testid="login-email-input"
                                />
                            </div>
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label
                                        htmlFor="password"
                                        className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-300"
                                    >
                                        Password
                                    </Label>
                                    <a
                                        href="/forgot-password"
                                        className="text-xs font-semibold text-teal-300 hover:text-teal-200"
                                    >
                                        Forgot?
                                    </a>
                                </div>
                                <div className="relative mt-2">
                                    <Input
                                        id="password"
                                        type={show ? "text" : "password"}
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Your password"
                                        className="h-12 rounded-xl bg-white/5 border-white/15 placeholder:text-slate-500 text-white focus-visible:ring-teal-500/40 focus-visible:border-teal-500/40 pr-11"
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShow((s) => !s)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                        aria-label="Toggle password visibility"
                                    >
                                        {show ? (
                                            <EyeOff className="h-4 w-4" />
                                        ) : (
                                            <Eye className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                                <Checkbox
                                    id="r"
                                    className="border-white/30 data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500"
                                />
                                <Label
                                    htmlFor="r"
                                    className="font-normal text-slate-300 cursor-pointer"
                                >
                                    Keep me logged in
                                </Label>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="luxury-btn mt-2 w-full h-12 rounded-2xl text-sm font-bold uppercase tracking-[0.14em] inline-flex items-center justify-center gap-2 disabled:opacity-70"
                                data-testid="login-submit-btn"
                            >
                                {loading ? "Logging in..." : "Log In"}
                                <ArrowRight className="h-4 w-4" />
                            </button>
                        </form>

                        <p className="mt-7 text-sm text-center text-slate-400">
                            Don&apos;t have an account?{" "}
                            <Link
                                to="/signup"
                                className="font-bold text-gold hover:text-yellow-300 transition-colors"
                            >
                                Sign up free →
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
